import React, { useState, useEffect } from "react";
import { format, parseISO, isSameDay, addDays, startOfWeek, endOfWeek, eachDayOfInterval, addDays as addDaysFn, subDays, subWeeks, subMonths } from "date-fns";
import { collection, query, where, getDocs, orderBy, getDoc, doc, updateDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { useFirebase } from "@/contexts/FirebaseContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, CheckCircle, Repeat, MapPin, BookOpen, Target, Info, Search, Filter, SortAsc, SortDesc, BarChart, ChevronLeft, ChevronRight, List, Loader2, AlertCircle, Download, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Link } from "react-router-dom";

// Buffer time configuration - 15 minutes between lessons for instructor break
const LESSON_BUFFER_MINUTES = 15;

interface Schedule {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  duration: number;
  status: string;
  instructorId: string;
  instructorName: string;
  studentId: string;
  enrollmentId: string;
  courseId: string;
  enrollmentStatus: string;
  courseName: string;
  courseType: string;
  courseSubtype?: string;
  totalHours?: number;
  hoursTaken?: number;
  hoursRemaining?: number;
  amount?: number;
  currency?: string;
  vehicleType?: string;
  licenseType?: string;
  previousLessons?: string;
  medicalConditions?: boolean;
  medicalDetails?: string;
  nervousAboutDriving?: boolean;
  specificGoals?: string;
  focusAreas?: string;
  isRecurring?: boolean;
  recurringGroupId?: string;
  recurringWeek?: number;
  totalRecurringWeeks?: number;
  recurringDay?: string;
  venue?: string;
}

interface FetchedSchedule {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  duration: number;
  status: string;
  instructorId: string;
  studentId: string;
  enrollmentId: string;
  isRecurring?: boolean;
  recurringGroupId?: string;
  recurringWeek?: number;
  totalRecurringWeeks?: number;
  recurringDay?: string;
  venue?: string;
}

interface FetchedEnrollment {
  id: string;
  userId: string;
  courseId: string;
  enrollmentStatus: string;
}

interface CourseData {
  title: string;
  type: string;
  subtype?: string;
  totalHours?: number;
  price?: number;
}

const StudentSchedule = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, schedule: null as Schedule | null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, schedule: null as Schedule | null });
  const [newDate, setNewDate] = useState<Date | null>(null);
  const [newTime, setNewTime] = useState("");
  const { currentUser, getUserByEmail } = useFirebase();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Add state for overview functionality
  const [overviewPeriod, setOverviewPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [selectedOverviewDate, setSelectedOverviewDate] = useState<Date>(new Date());
  const [isOverviewCollapsed, setIsOverviewCollapsed] = useState(false);

  // Add state for report functionality
  const [reportPeriod, setReportPeriod] = useState<'day' | 'week' | 'month'>('week');
  const [reportDate, setReportDate] = useState<Date>(new Date());
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Add state for viewing student details
  const [viewDetailsDialog, setViewDetailsDialog] = useState<{ open: boolean; schedule: Schedule | null }>({
    open: false,
    schedule: null
  });
  const [studentDetails, setStudentDetails] = useState<any>(null);
  const [isLoadingStudentDetails, setIsLoadingStudentDetails] = useState(false);
  const [studentDetailsError, setStudentDetailsError] = useState<string | null>(null);

  const timeSlots = [
    "09:00", "10:00", "11:00", "12:00", "13:00", 
    "14:00", "15:00", "16:00", "17:00"
  ];

  // Helper functions for overview period calculations
  const getDateRangeForPeriod = (date: Date, period: 'day' | 'week' | 'month') => {
    const start = new Date(date);
    const end = new Date(date);
    
    switch (period) {
      case 'day':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        // Start from Monday of the week
        const dayOfWeek = start.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start.setDate(start.getDate() - daysToMonday);
        start.setHours(0, 0, 0, 0);
        // End on Sunday
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(end.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
    }
    
    return { start, end };
  };

  const getEventsForPeriod = (period: 'day' | 'week' | 'month', date: Date) => {
    const { start, end } = getDateRangeForPeriod(date, period);
    return schedules.filter(schedule => {
      const scheduleDate = new Date(schedule.date);
      return scheduleDate >= start && scheduleDate <= end;
    });
  };

  const getOverviewTitle = (period: 'day' | 'week' | 'month', date: Date) => {
    switch (period) {
      case 'day':
        return format(date, 'EEEE, MMMM d, yyyy');
      case 'week':
        const { start, end } = getDateRangeForPeriod(date, period);
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
      case 'month':
        return format(date, 'MMMM yyyy');
      default:
        return format(date, 'EEEE, MMMM d, yyyy');
    }
  };

  const getOverviewStats = (period: 'day' | 'week' | 'month', date: Date) => {
    const periodEvents = getEventsForPeriod(period, date);
    
    const totalLessons = periodEvents.length;
    const totalHours = periodEvents.reduce((total, event) => total + (event.duration || 1), 0);
    const confirmedLessons = periodEvents.filter(event => event.status === 'confirmed').length;
    const pendingLessons = periodEvents.filter(event => event.status === 'pending').length;
    const completedLessons = periodEvents.filter(event => event.status === 'completed').length;
    
    return {
      totalLessons,
      totalHours,
      confirmedLessons,
      pendingLessons,
      completedLessons
    };
  };

  const navigateOverviewDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedOverviewDate);
    switch (overviewPeriod) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }
    setSelectedOverviewDate(newDate);
  };

  const navigateToLastPeriod = (period: 'week' | 'month') => {
    const newDate = new Date();
    switch (period) {
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
    }
    setSelectedOverviewDate(newDate);
  };

  const handleViewDetails = async (schedule: Schedule) => {
    setViewDetailsDialog({ open: true, schedule });
    setIsLoadingStudentDetails(true);
    setStudentDetailsError(null);

    try {
      const db = getFirestore();
      
      // Fetch student details from Firestore
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('id', '==', schedule.studentId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Student not found');
      }

      const studentDoc = querySnapshot.docs[0];
      const studentData = studentDoc.data();

      // Fetch student's enrollments
      const enrollmentsRef = collection(db, 'enrollments');
      const enrollmentsQuery = query(
        enrollmentsRef,
        where('studentId', '==', schedule.studentId)
      );
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      
      // Fetch student's completed lessons
      const lessonsRef = collection(db, 'lessons');
      const lessonsQuery = query(
        lessonsRef,
        where('studentId', '==', schedule.studentId),
        where('status', '==', 'completed')
      );
      const lessonsSnapshot = await getDocs(lessonsQuery);
      const completedLessons = lessonsSnapshot.size;

      // Create student details object with all available information
      setStudentDetails({
        id: studentDoc.id,
        firstName: studentData.firstName || '',
        lastName: studentData.lastName || '',
        email: studentData.email || '',
        phone: studentData.phone || 'Not provided',
        address: studentData.address || 'Not provided',
        postcode: studentData.postcode || 'Not provided',
        gearbox: studentData.gearbox || 'Not specified',
        licenseNumber: studentData.licenseNumber || 'Not provided',
        progress: {
          completedLessons: completedLessons,
          totalLessons: enrollmentsSnapshot.size,
          lastLessonDate: studentData.lastLessonDate || 'No lessons completed'
        }
      });
    } catch (error) {
      console.error('Error fetching student details:', error);
      setStudentDetailsError(error instanceof Error ? error.message : 'Failed to load student details');
    } finally {
      setIsLoadingStudentDetails(false);
    }
  };

  // PDF Report Generation
  const generatePDFReport = async (period: 'day' | 'week' | 'month', date: Date) => {
    setIsGeneratingReport(true);
    try {
      const periodEvents = getEventsForPeriod(period, date);
      const stats = getOverviewStats(period, date);
      
      // Create HTML content for the report with enhanced styling
      const reportHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Student Schedule Report - ${getOverviewTitle(period, date)}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0; 
              background: #f8fafc;
              color: #1e293b;
            }
            .container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }
            .header {
              background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
              color: white;
              padding: 40px 30px;
              text-align: center;
              position: relative;
              overflow: hidden;
            }
            .header::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.1"/><circle cx="10" cy="60" r="0.5" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
              opacity: 0.3;
            }
            .header h1 {
              font-size: 2.5em;
              font-weight: 700;
              margin-bottom: 10px;
              position: relative;
              z-index: 1;
            }
            .header h2 {
              font-size: 1.3em;
              font-weight: 400;
              opacity: 0.9;
              margin-bottom: 15px;
              position: relative;
              z-index: 1;
            }
            .header p {
              font-size: 0.9em;
              opacity: 0.8;
              position: relative;
              z-index: 1;
            }
            .content {
              padding: 40px 30px;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 20px;
              margin-bottom: 40px;
            }
            .stat-card {
              background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 25px 20px;
              text-align: center;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
              transition: transform 0.2s ease;
            }
            .stat-card:hover {
              transform: translateY(-2px);
            }
            .stat-number {
              font-size: 2.5em;
              font-weight: 700;
              color: #2563eb;
              margin-bottom: 8px;
              display: block;
            }
            .stat-label {
              font-size: 0.9em;
              color: #64748b;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .section {
              margin-bottom: 40px;
            }
            .section-title {
              font-size: 1.5em;
              font-weight: 600;
              color: #1e293b;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e2e8f0;
              position: relative;
            }
            .section-title::after {
              content: '';
              position: absolute;
              bottom: -2px;
              left: 0;
              width: 60px;
              height: 2px;
              background: #2563eb;
            }
            .lessons-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            .lessons-table th {
              background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
              color: white;
              padding: 15px 12px;
              text-align: left;
              font-weight: 600;
              font-size: 0.9em;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .lessons-table td {
              padding: 12px;
              border-bottom: 1px solid #e2e8f0;
              font-size: 0.9em;
            }
            .lessons-table tr:nth-child(even) {
              background: #f8fafc;
            }
            .lessons-table tr:hover {
              background: #f1f5f9;
            }
            .status-badge {
              padding: 4px 8px;
              border-radius: 6px;
              font-size: 0.8em;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .status-confirmed {
              background: #dcfce7;
              color: #166534;
            }
            .status-pending {
              background: #fef3c7;
              color: #92400e;
            }
            .status-completed {
              background: #dbeafe;
              color: #1e40af;
            }
            .status-cancelled {
              background: #fee2e2;
              color: #991b1b;
            }
            .empty-state {
              text-align: center;
              padding: 60px 20px;
              color: #64748b;
            }
            .empty-state h3 {
              font-size: 1.3em;
              margin-bottom: 10px;
              color: #475569;
            }
            .empty-state p {
              font-size: 0.95em;
              line-height: 1.6;
            }
            .footer {
              background: #f8fafc;
              padding: 30px;
              text-align: center;
              border-top: 1px solid #e2e8f0;
            }
            .footer p {
              color: #64748b;
              font-size: 0.9em;
            }
            @media print {
              body { background: white; }
              .container { box-shadow: none; }
              .stat-card:hover { transform: none; }
              .lessons-table tr:hover { background: inherit; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>📅 Student Schedule Report</h1>
              <h2>${getOverviewTitle(period, date)}</h2>
              <p>Generated on ${format(new Date(), 'EEEE, MMMM d, yyyy \'at\' h:mm a')}</p>
            </div>
            
            <div class="content">
              <div class="section">
                <h3 class="section-title">📊 Summary Statistics</h3>
                <div class="stats-grid">
                  <div class="stat-card">
                    <span class="stat-number">${stats.totalLessons}</span>
                    <div class="stat-label">Total Lessons</div>
                  </div>
                  <div class="stat-card">
                    <span class="stat-number">${stats.totalHours}</span>
                    <div class="stat-label">Total Hours</div>
                  </div>
                  <div class="stat-card">
                    <span class="stat-number">${stats.confirmedLessons}</span>
                    <div class="stat-label">Confirmed</div>
                  </div>
                  <div class="stat-card">
                    <span class="stat-number">${stats.pendingLessons}</span>
                    <div class="stat-label">Pending</div>
                  </div>
                  <div class="stat-card">
                    <span class="stat-number">${stats.completedLessons}</span>
                    <div class="stat-label">Completed</div>
                  </div>
                </div>
              </div>
              
              <div class="section">
                <h3 class="section-title">📋 Lesson Details</h3>
                ${periodEvents.length === 0 ? `
                  <div class="empty-state">
                    <h3>No Lessons Found</h3>
                    <p>No lessons were scheduled for ${period === 'day' ? 'this day' : period === 'week' ? 'this week' : 'this month'}.</p>
                  </div>
                ` : `
                  <table class="lessons-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Course</th>
                        <th>Instructor</th>
                        <th>Duration</th>
                        <th>Status</th>
                        <th>Venue</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${periodEvents.map(lesson => `
                        <tr>
                          <td>${format(new Date(lesson.date), 'EEEE, MMMM d, yyyy')}</td>
                          <td>${lesson.startTime} - ${lesson.endTime}</td>
                          <td>${lesson.courseName}</td>
                          <td>${lesson.instructorName}</td>
                          <td>${lesson.duration} hour${lesson.duration !== 1 ? 's' : ''}</td>
                          <td><span class="status-badge status-${lesson.status}">${lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1)}</span></td>
                          <td>${lesson.venue || 'Not specified'}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                `}
              </div>
            </div>
            
            <div class="footer">
              <p>© ${new Date().getFullYear()} DriveWise School. This report was generated automatically.</p>
            </div>
          </div>
        </body>
        </html>
      `;

      // Create blob and download
      const blob = new Blob([reportHTML], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student-schedule-report-${period}-${format(date, 'yyyy-MM-dd')}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Report Generated",
        description: `Your ${period} schedule report has been downloaded successfully.`,
      });
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        title: "Error",
        description: "Failed to generate report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  useEffect(() => {
    const fetchSchedules = async () => {
      if (!currentUser) return;
      setLoading(true);
      try {
        const db = getFirestore();
        const schedulesRef = collection(db, "schedules");
        const q = query(
          schedulesRef,
          where("studentId", "==", currentUser.uid),
          where("status", "in", ["pending", "confirmed", "completed"])
        );
        const querySnapshot = await getDocs(q);
        
        // Fetch all enrollments for the student
        const enrollmentsRef = collection(db, "enrollments");
        const enrollmentsQuery = query(
          enrollmentsRef,
          where("userId", "==", currentUser.uid),
          where("status", "in", ["active", "pending", "enrolled", "assigned"])
        );
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        
        // Create a map of enrollments by ID for quick lookup
        const enrollmentsMap = new Map();
        for (const enrollmentDoc of enrollmentsSnapshot.docs) {
          const enrollment = enrollmentDoc.data();
          // Fetch course details to get the course name
          const courseRef = doc(db, "courses", enrollment.courseId);
          const courseSnap = await getDoc(courseRef);
          const courseData = courseSnap.exists() ? courseSnap.data() as CourseData : null;
          
          // Check if this is a combined Individual/Blog lesson
          const isCombinedLesson = enrollment.courseType === "individual" || 
                                 (enrollment.courseName && 
                                  (enrollment.courseName.toLowerCase().includes("blog") || 
                                   enrollment.courseName.toLowerCase().includes("combined")));
          
          enrollmentsMap.set(enrollmentDoc.id, {
            ...enrollment,
            courseName: isCombinedLesson ? "Combined Driving Lesson" : (courseData?.title || enrollment.courseName || 'Unknown Course')
          });
        }
        
        const fetchedSchedules = querySnapshot.docs.map(doc => {
          const scheduleData = doc.data();
          const enrollment = enrollmentsMap.get(scheduleData.enrollmentId);
          
          return {
            id: doc.id,
            ...scheduleData,
            // Add course information from enrollment
            courseName: enrollment?.courseName || 'Unknown Course',
            courseType: enrollment?.courseType || '',
            courseSubtype: enrollment?.subtype || '',
            totalHours: enrollment?.totalHours || 0,
            hoursTaken: enrollment?.hoursTaken || 0,
            hoursRemaining: enrollment?.hoursRemaining || 0,
            amount: enrollment?.amount || 0,
            currency: enrollment?.currency || 'EUR',
            vehicleType: enrollment?.vehicleType || '',
            licenseType: enrollment?.licenseType || '',
            previousLessons: enrollment?.previousLessons || '0',
            medicalConditions: enrollment?.medicalConditions || false,
            medicalDetails: enrollment?.medicalDetails || '',
            nervousAboutDriving: enrollment?.nervousAboutDriving || false,
            specificGoals: enrollment?.specificGoals || '',
            focusAreas: enrollment?.focusAreas || ''
          };
        }) as Schedule[];
        
        // Sort by date in memory
        fetchedSchedules.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        console.log("Fetched schedules with course info:", fetchedSchedules);
        setSchedules(fetchedSchedules);
      } catch (error) {
        console.error("Error fetching schedules:", error);
        toast({
          title: "Error",
          description: "Failed to load schedules. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [currentUser, toast]);

  // Filter and sort schedules
  const filteredAndSortedSchedules = schedules
    .filter((schedule) => {
      // Search by course name or instructor name
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        schedule.courseName.toLowerCase().includes(searchLower) ||
        schedule.instructorName.toLowerCase().includes(searchLower);

      // Filter by status
      const matchesFilter = filter === "all" || schedule.status === filter;

      // Filter by date
      const matchesDate = !selectedDate || format(new Date(schedule.date), "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");

      return matchesSearch && matchesFilter && matchesDate;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case "date":
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "course":
          comparison = a.courseName.localeCompare(b.courseName);
          break;
        case "time":
          comparison = a.startTime.localeCompare(b.startTime);
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        default:
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

  // Group schedules if needed
  const filteredGroupedSchedules = filter === "groupByCourse" 
    ? filteredAndSortedSchedules.reduce((groups, schedule) => {
        const key = schedule.courseName;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(schedule);
        return groups;
      }, {} as { [key: string]: Schedule[] })
    : filter === "groupByDate"
    ? filteredAndSortedSchedules.reduce((groups, schedule) => {
        const key = format(new Date(schedule.date), "yyyy-MM-dd");
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(schedule);
        return groups;
      }, {} as { [key: string]: Schedule[] })
    : null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Group schedules by recurring group
  const recurringGroupedSchedules = filteredAndSortedSchedules.reduce((groups, schedule) => {
    if (schedule.isRecurring && schedule.recurringGroupId) {
      if (!groups[schedule.recurringGroupId]) {
        groups[schedule.recurringGroupId] = [];
      }
      groups[schedule.recurringGroupId].push(schedule);
    } else {
      if (!groups['non-recurring']) {
        groups['non-recurring'] = [];
      }
      groups['non-recurring'].push(schedule);
    }
    return groups;
  }, {} as { [key: string]: Schedule[] });

  // Sort recurring groups by date
  const sortedGroups = Object.entries(recurringGroupedSchedules).sort(([keyA, schedulesA], [keyB, schedulesB]) => {
    if (keyA === 'non-recurring') return 1;
    if (keyB === 'non-recurring') return -1;
    return new Date(schedulesA[0].date).getTime() - new Date(schedulesB[0].date).getTime();
  });

  const handleEditRecurring = async (schedule: Schedule, editAll: boolean = false) => {
    if (!newDate || !newTime) return;

    // Check 24-hour restriction for rescheduling
    const now = new Date();
    const lessonDate = new Date(schedule.date);
    const hoursDiff = (lessonDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    if (hoursDiff < 24) {
      toast({
        title: "Cannot Reschedule",
        description: "You cannot reschedule a lesson less than 24 hours before its scheduled time.",
        variant: "destructive",
      });
      return;
    }

    try {
      const db = getFirestore();
      
      // Check availability before proceeding with reschedule
      const isAvailable = await checkRescheduleAvailability(
        format(newDate, 'yyyy-MM-dd'),
        newTime,
        schedule.duration || 1,
        schedule.instructorId,
        schedule.studentId,
        schedule.id
      );
      
      if (!isAvailable) {
        return; // Error message already shown by checkRescheduleAvailability
      }
      
      if (editAll && schedule.recurringGroupId) {
        // Edit all lessons in the recurring series
        const groupSchedules = schedules.filter(s => s.recurringGroupId === schedule.recurringGroupId);
        
        // Calculate the date difference to maintain the pattern
        const originalDate = new Date(schedule.date);
        const newDateObj = new Date(newDate);
        const dateDiff = newDateObj.getTime() - originalDate.getTime();
        
        // Check availability for all lessons in the series
        for (const s of groupSchedules) {
          const newLessonDate = new Date(new Date(s.date).getTime() + dateDiff);
          const formattedDate = format(newLessonDate, 'yyyy-MM-dd');
          
          const isGroupAvailable = await checkRescheduleAvailability(
            formattedDate,
            newTime,
            s.duration || 1,
            s.instructorId,
            s.studentId,
            s.id
          );
          
          if (!isGroupAvailable) {
            toast({
              title: "Series Reschedule Failed",
              description: `Cannot reschedule all lessons in the series. Conflict found on ${format(newLessonDate, 'MMMM d, yyyy')}.`,
              variant: "destructive",
            });
            return;
          }
        }
        
        // Update all lessons in the series
        await Promise.all(groupSchedules.map(async (s) => {
          const scheduleRef = doc(db, "schedules", s.id);
          const newLessonDate = new Date(new Date(s.date).getTime() + dateDiff);
          
          // Calculate end time
          const [hours, minutes] = newTime.split(':').map(Number);
          const startMinutes = hours * 60 + minutes;
          const endMinutes = startMinutes + ((s.duration || 1) * 60);
          const endHours = Math.floor(endMinutes / 60);
          const endMins = endMinutes % 60;
          const endTime = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
          
          await updateDoc(scheduleRef, {
            date: format(newLessonDate, 'yyyy-MM-dd'),
            startTime: newTime,
            endTime: endTime,
            startMinutes: startMinutes,
            endMinutes: endMinutes,
            status: "confirmed", // Keep confirmed status
            updatedAt: new Date().toISOString()
          });
        }));

        // Update local state
        setSchedules(schedules.map(s => 
          s.recurringGroupId === schedule.recurringGroupId
            ? {
                ...s,
                date: format(new Date(new Date(s.date).getTime() + dateDiff), 'yyyy-MM-dd'),
                startTime: newTime,
                status: "confirmed"
              }
            : s
        ));

        toast({
          title: "Series Updated",
          description: "All lessons in the recurring series have been rescheduled successfully.",
        });
      } else {
        // Edit single lesson
        const scheduleRef = doc(db, "schedules", schedule.id);
        
        // Calculate end time
        const [hours, minutes] = newTime.split(':').map(Number);
        const startMinutes = hours * 60 + minutes;
        const endMinutes = startMinutes + ((schedule.duration || 1) * 60);
        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        const endTime = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
        
        await updateDoc(scheduleRef, {
          date: format(newDate, 'yyyy-MM-dd'),
          startTime: newTime,
          endTime: endTime,
          startMinutes: startMinutes,
          endMinutes: endMinutes,
          status: "confirmed", // Keep confirmed status
          updatedAt: new Date().toISOString()
        });

        // Update local state
        setSchedules(schedules.map(s => 
          s.id === schedule.id
            ? { 
                ...s, 
                date: format(newDate, 'yyyy-MM-dd'), 
                startTime: newTime,
                endTime: endTime,
                status: "confirmed" 
              }
            : s
        ));

        toast({
          title: "Lesson Updated",
          description: "Your lesson has been rescheduled successfully.",
        });
      }

      setEditDialog({ open: false, schedule: null });
    } catch (error) {
      console.error("Error updating schedule:", error);
      toast({
        title: "Error",
        description: "Failed to update schedule. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Add comprehensive availability checking function
  const checkRescheduleAvailability = async (
    date: string, 
    startTime: string, 
    duration: number, 
    instructorId: string,
    studentId: string,
    excludeScheduleId: string
  ): Promise<boolean> => {
    try {
      const db = getFirestore();
      const startDateTime = new Date(`${date}T${startTime}`);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 60 * 1000);

      console.log('🔍 Checking availability for reschedule:', {
        date,
        startTime,
        duration,
        instructorId,
        studentId,
        excludeScheduleId
      });

      // 1. Check instructor's unavailability from instructor_availability collection
      const instructorAvailabilityRef = doc(db, 'instructor_availability', 
        // Get instructor email from schedules to match availability doc
        await getInstructorEmail(instructorId)
      );
      const availabilityDoc = await getDoc(instructorAvailabilityRef);
      
      if (availabilityDoc.exists()) {
        const unavailableSlots = availabilityDoc.data().unavailableSlots || [];
        const dateUnavailableSlots = unavailableSlots.filter((slot: any) => slot.date === date);
        
        for (const slot of dateUnavailableSlots) {
          if (slot.isFullDay) {
            toast({
              title: "Instructor Unavailable",
              description: "Your instructor is unavailable for the entire day.",
              variant: "destructive",
            });
            return false;
          }

          const slotStart = new Date(`${date}T${slot.startTime}`);
          const slotEnd = new Date(`${date}T${slot.endTime}`);
          
          if (
            (startDateTime >= slotStart && startDateTime < slotEnd) ||
            (endDateTime > slotStart && endDateTime <= slotEnd) ||
            (startDateTime <= slotStart && endDateTime >= slotEnd)
          ) {
            toast({
              title: "Instructor Unavailable",
              description: `Your instructor is unavailable from ${slot.startTime} to ${slot.endTime}.`,
              variant: "destructive",
            });
            return false;
          }
        }
      }

      // 2. Check instructor's existing bookings with other students
      const instructorBookingsQuery = query(
        collection(db, "schedules"),
        where("instructorId", "==", instructorId),
        where("date", "==", date),
        where("status", "in", ["pending", "confirmed"])
      );
      
      const instructorBookingsSnapshot = await getDocs(instructorBookingsQuery);
      const instructorBookings = instructorBookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Array<{ id: string; startTime: string; duration: number; studentName: string }>;

      for (const booking of instructorBookings) {
        // Skip the current lesson being rescheduled
        if (booking.id === excludeScheduleId) {
          continue;
        }
        
        const bookingStart = new Date(`${date}T${booking.startTime}`);
        const bookingEnd = new Date(bookingStart.getTime() + booking.duration * 60 * 60 * 1000);
        // Add buffer time to existing booking - instructors need 15-minute break
        const bookingEndWithBuffer = new Date(bookingEnd.getTime() + (LESSON_BUFFER_MINUTES * 60 * 1000));
        
        if (
          (startDateTime >= bookingStart && startDateTime < bookingEndWithBuffer) ||
          (endDateTime > bookingStart && endDateTime <= bookingEndWithBuffer) ||
          (startDateTime <= bookingStart && endDateTime >= bookingEndWithBuffer)
        ) {
          toast({
            title: "Time Slot Unavailable",
            description: `Your instructor already has a lesson from ${booking.startTime} to ${format(bookingEnd, 'HH:mm')} with another student. Please allow 15-minute buffer time.`,
            variant: "destructive",
          });
          return false;
        }
      }

      // 3. Check student's own existing bookings with any instructor
      const studentBookingsQuery = query(
        collection(db, "schedules"),
        where("studentId", "==", studentId),
        where("date", "==", date),
        where("status", "in", ["pending", "confirmed"])
      );
      
      const studentBookingsSnapshot = await getDocs(studentBookingsQuery);
      const studentBookings = studentBookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Array<{ id: string; startTime: string; duration: number; instructorName: string }>;

      for (const booking of studentBookings) {
        // Skip the current lesson being rescheduled
        if (booking.id === excludeScheduleId) {
          continue;
        }
        
        const bookingStart = new Date(`${date}T${booking.startTime}`);
        const bookingEnd = new Date(bookingStart.getTime() + booking.duration * 60 * 60 * 1000);
        // Add buffer time to student conflict check - students need 15-minute break between lessons
        const bookingEndWithBuffer = new Date(bookingEnd.getTime() + (LESSON_BUFFER_MINUTES * 60 * 1000));
        
        if (
          (startDateTime >= bookingStart && startDateTime < bookingEndWithBuffer) ||
          (endDateTime > bookingStart && endDateTime <= bookingEndWithBuffer) ||
          (startDateTime <= bookingStart && endDateTime >= bookingEndWithBuffer)
        ) {
          toast({
            title: "You Have a Conflict",
            description: `You already have a lesson from ${booking.startTime} to ${format(bookingEnd, 'HH:mm')} with ${booking.instructorName}. Please allow 15-minute buffer time between lessons.`,
            variant: "destructive",
          });
          return false;
        }
      }

      console.log('✅ Availability check passed for reschedule');
      return true;

    } catch (error) {
      console.error("Error checking reschedule availability:", error);
      toast({
        title: "Error",
        description: "Failed to check availability. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Helper function to get instructor email for availability checking
  const getInstructorEmail = async (instructorId: string): Promise<string> => {
    try {
      const db = getFirestore();
      const instructorRef = doc(db, 'users', instructorId);
      const instructorDoc = await getDoc(instructorRef);
      
      if (instructorDoc.exists()) {
        return instructorDoc.data().email || '';
      }
      return '';
    } catch (error) {
      console.error("Error getting instructor email:", error);
      return '';
    }
  };

  const handleCancelRecurring = async (schedule: Schedule, cancelAll: boolean = false) => {
    try {
      const db = getFirestore();
      const now = new Date();
      console.log('Cancellation attempt:', {
        scheduleId: schedule.id,
        lessonDate: schedule.date,
        currentTime: now.toISOString(),
        cancelAll,
        isRecurring: !!schedule.recurringGroupId
      });
      
      if (cancelAll && schedule.recurringGroupId) {
        // Cancel all lessons in the recurring series (only those more than 24h away)
        const groupSchedules = schedules.filter(s => s.recurringGroupId === schedule.recurringGroupId);
        console.log('Recurring group schedules:', {
          groupId: schedule.recurringGroupId,
          totalLessons: groupSchedules.length,
          schedules: groupSchedules.map(s => ({
            id: s.id,
            date: s.date,
            hoursUntilLesson: (new Date(s.date).getTime() - now.getTime()) / (1000 * 60 * 60)
          }))
        });

        const lessonsToCancel = groupSchedules.filter(s => {
          const hoursUntilLesson = (new Date(s.date).getTime() - now.getTime()) / (1000 * 60 * 60);
          console.log(`Lesson ${s.id} hours until lesson:`, hoursUntilLesson);
          return hoursUntilLesson >= 24;
        });

        console.log('Lessons eligible for cancellation:', {
          total: lessonsToCancel.length,
          lessons: lessonsToCancel.map(s => ({
            id: s.id,
            date: s.date,
            duration: s.duration
          }))
        });

        if (lessonsToCancel.length === 0) {
          console.log('No lessons eligible for cancellation - all within 24 hours');
          toast({
            title: "Cannot Cancel",
            description: "All lessons in this series are less than 24 hours away and cannot be cancelled.",
            variant: "destructive",
          });
          return;
        }

        await Promise.all(lessonsToCancel.map(async (s) => {
          console.log(`Processing cancellation for lesson ${s.id}:`, {
            date: s.date,
            duration: s.duration,
            enrollmentId: s.enrollmentId
          });

          const scheduleRef = doc(db, "schedules", s.id);
          await updateDoc(scheduleRef, { status: "cancelled" });
          console.log(`Updated schedule status to cancelled for lesson ${s.id}`);

          // Refund hours
          if (s.enrollmentId) {
            const enrollmentRef = doc(db, "enrollments", s.enrollmentId);
            const enrollmentSnap = await getDoc(enrollmentRef);
            if (enrollmentSnap.exists()) {
              const enrollment = enrollmentSnap.data();
              console.log('Current enrollment state:', {
                enrollmentId: s.enrollmentId,
                currentHoursRemaining: enrollment.hoursRemaining,
                currentHoursTaken: enrollment.hoursTaken,
                lessonDuration: s.duration
              });

              const newHoursRemaining = (enrollment.hoursRemaining || 0) + (s.duration || 1);
              const newHoursTaken = Math.max((enrollment.hoursTaken || 0) - (s.duration || 1), 0);
              
              console.log('Updating enrollment hours:', {
                enrollmentId: s.enrollmentId,
                newHoursRemaining,
                newHoursTaken
              });

              await updateDoc(enrollmentRef, {
                hoursRemaining: newHoursRemaining,
                hoursTaken: newHoursTaken,
                updatedAt: new Date().toISOString(),
              });
              console.log(`Successfully updated enrollment ${s.enrollmentId} hours`);
            } else {
              console.log(`Enrollment ${s.enrollmentId} not found`);
            }
          }
        }));

        setSchedules(schedules.map(s => 
          s.recurringGroupId === schedule.recurringGroupId && (new Date(s.date).getTime() - now.getTime()) / (1000 * 60 * 60) >= 24
            ? { ...s, status: "cancelled" }
            : s
        ));
        toast({
          title: "Series Cancelled",
          description: "All eligible lessons in the recurring series have been cancelled.",
        });
      } else {
        // Cancel single lesson
        const lessonDate = new Date(schedule.date);
        const hoursDiff = (lessonDate.getTime() - now.getTime()) / (1000 * 60 * 60);
        console.log('Single lesson cancellation check:', {
          lessonId: schedule.id,
          lessonDate: schedule.date,
          currentTime: now.toISOString(),
          hoursUntilLesson: hoursDiff
        });

        if (hoursDiff < 24) {
          console.log('Lesson cannot be cancelled - less than 24 hours away');
          toast({
            title: "Cannot Cancel",
            description: "You cannot cancel a lesson less than 24 hours before its scheduled time.",
            variant: "destructive",
          });
          return;
        }

        console.log('Proceeding with single lesson cancellation:', {
          lessonId: schedule.id,
          enrollmentId: schedule.enrollmentId,
          duration: schedule.duration
        });

        const scheduleRef = doc(db, "schedules", schedule.id);
        await updateDoc(scheduleRef, { status: "cancelled" });
        console.log(`Updated schedule status to cancelled for lesson ${schedule.id}`);

        // Refund hours
        if (schedule.enrollmentId) {
          const enrollmentRef = doc(db, "enrollments", schedule.enrollmentId);
          const enrollmentSnap = await getDoc(enrollmentRef);
          if (enrollmentSnap.exists()) {
            const enrollment = enrollmentSnap.data();
            console.log('Current enrollment state:', {
              enrollmentId: schedule.enrollmentId,
              currentHoursRemaining: enrollment.hoursRemaining,
              currentHoursTaken: enrollment.hoursTaken,
              lessonDuration: schedule.duration
            });

            const newHoursRemaining = (enrollment.hoursRemaining || 0) + (schedule.duration || 1);
            const newHoursTaken = Math.max((enrollment.hoursTaken || 0) - (schedule.duration || 1), 0);
            
            console.log('Updating enrollment hours:', {
              enrollmentId: schedule.enrollmentId,
              newHoursRemaining,
              newHoursTaken
            });

            await updateDoc(enrollmentRef, {
              hoursRemaining: newHoursRemaining,
              hoursTaken: newHoursTaken,
              updatedAt: new Date().toISOString(),
            });
            console.log(`Successfully updated enrollment ${schedule.enrollmentId} hours`);
          } else {
            console.log(`Enrollment ${schedule.enrollmentId} not found`);
          }
        }
        setSchedules(schedules.map(s => 
          s.id === schedule.id
            ? { ...s, status: "cancelled" }
            : s
        ));
        toast({
          title: "Lesson Cancelled",
          description: "Your lesson has been cancelled successfully.",
        });
      }
      setCancelDialog({ open: false, schedule: null });
    } catch (error) {
      console.error("Error cancelling schedule:", error);
      toast({
        title: "Error",
        description: "Failed to cancel lesson. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex justify-center items-center h-64">
          <p>Loading schedules...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">My Schedules</h1>
          <p className="text-gray-500 mt-2">View your upcoming driving lessons</p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by course or instructor..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Date Filter */}
            <div>
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => setSelectedDate(date)}
                placeholderText="Filter by date..."
                className="w-full p-2 border rounded-md"
                dateFormat="yyyy-MM-dd"
                isClearable
              />
            </div>

            {/* Status Filter */}
            <div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="groupByCourse">Group by Course</SelectItem>
                  <SelectItem value="groupByDate">Group by Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort */}
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="course">Course</SelectItem>
                  <SelectItem value="time">Time</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              >
                {sortOrder === "asc" ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mt-4 pt-4 border-t text-sm text-gray-600">
            Showing {filteredAndSortedSchedules.length} of {schedules.length} schedules
            {search && ` matching "${search}"`}
            {selectedDate && ` on ${format(selectedDate, "MMMM d, yyyy")}`}
            {filter !== "all" && filter !== "groupByCourse" && filter !== "groupByDate" && ` with status "${filter}"`}
          </div>
        </div>

        {/* Student Overview Section */}
        <div className="mb-6">
          <Card className="border-t-4 border-drivewise-500">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <CardTitle className="text-xl flex items-center gap-2">
                    <BarChart className="h-5 w-5 text-drivewise-600" />
                    My Overview - {getOverviewTitle(overviewPeriod, selectedOverviewDate)}
                  </CardTitle>
                  
                  {/* Period Selector */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant={overviewPeriod === 'day' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setOverviewPeriod('day')}
                    >
                      Day
                    </Button>
                    <Button
                      variant={overviewPeriod === 'week' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setOverviewPeriod('week')}
                    >
                      Week
                    </Button>
                    <Button
                      variant={overviewPeriod === 'month' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setOverviewPeriod('month')}
                    >
                      Month
                    </Button>
                  </div>
                </div>
                
                {/* Navigation */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateOverviewDate('prev')}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedOverviewDate(new Date())}
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateToLastPeriod('week')}
                  >
                    Last Week
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateToLastPeriod('month')}
                  >
                    Last Month
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateOverviewDate('next')}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Report Generation Section */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Generate Report:</span>
                  <div className="flex items-center gap-2">
                    <Select value={reportPeriod} onValueChange={(value: 'day' | 'week' | 'month') => setReportPeriod(value)}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Daily</SelectItem>
                        <SelectItem value="week">Weekly</SelectItem>
                        <SelectItem value="month">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <DatePicker
                      selected={reportDate}
                      onChange={(date: Date | null) => date && setReportDate(date)}
                      className="w-32 p-2 border rounded-md text-sm"
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select date"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => generatePDFReport(reportPeriod, reportDate)}
                      disabled={isGeneratingReport}
                      className="flex items-center gap-2"
                    >
                      {isGeneratingReport ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      {isGeneratingReport ? 'Generating...' : 'Download Report'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              {/* Overview Statistics */}
              {(() => {
                const stats = getOverviewStats(overviewPeriod, selectedOverviewDate);
                const periodEvents = getEventsForPeriod(overviewPeriod, selectedOverviewDate);
                
                return (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-blue-700">Total Lessons</p>
                              <p className="text-2xl font-bold text-blue-900">
                                {stats.totalLessons}
                              </p>
                            </div>
                            <div className="h-12 w-12 bg-blue-200 rounded-full flex items-center justify-center">
                              <Calendar className="h-6 w-6 text-blue-700" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-green-700">Total Hours</p>
                              <p className="text-2xl font-bold text-green-900">
                                {stats.totalHours}
                              </p>
                            </div>
                            <div className="h-12 w-12 bg-green-200 rounded-full flex items-center justify-center">
                              <Clock className="h-6 w-6 text-green-700" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-purple-700">Confirmed</p>
                              <p className="text-2xl font-bold text-purple-900">
                                {stats.confirmedLessons}
                              </p>
                            </div>
                            <div className="h-12 w-12 bg-purple-200 rounded-full flex items-center justify-center">
                              <CheckCircle className="h-6 w-6 text-purple-700" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-orange-700">Pending</p>
                              <p className="text-2xl font-bold text-orange-900">
                                {stats.pendingLessons}
                              </p>
                            </div>
                            <div className="h-12 w-12 bg-orange-200 rounded-full flex items-center justify-center">
                              <AlertCircle className="h-6 w-6 text-orange-700" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Lessons List */}
                    <div className="space-y-4">
                      <div 
                        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                        onClick={() => setIsOverviewCollapsed(!isOverviewCollapsed)}
                      >
                        <h4 className="text-lg font-semibold flex items-center gap-2">
                          <List className="h-5 w-5 text-drivewise-600" />
                          {overviewPeriod === 'day' ? 'Today' : overviewPeriod === 'week' ? 'This Week' : 'This Month'}s Lessons
                          <span className="text-sm font-normal text-gray-500">
                            ({periodEvents.length} lessons)
                          </span>
                        </h4>
                        {isOverviewCollapsed ? (
                          <ChevronRight className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronLeft className="h-5 w-5 text-gray-500" />
                        )}
                      </div>
                      
                      {!isOverviewCollapsed && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          {(() => {
                            const sortedEvents = periodEvents
                              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                            if (sortedEvents.length === 0) {
                              return (
                                <Card className="col-span-full">
                                  <CardContent className="p-6 text-center">
                                    <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                    <h5 className="text-lg font-medium text-gray-900 mb-2">
                                      No Lessons {overviewPeriod === 'day' ? 'Today' : overviewPeriod === 'week' ? 'This Week' : 'This Month'}
                                    </h5>
                                    <p className="text-gray-500 mb-4">
                                      {overviewPeriod === 'day' ? 'You have no lessons scheduled for today.' : 
                                       overviewPeriod === 'week' ? 'You have no lessons scheduled this week.' : 
                                       'You have no lessons scheduled this month.'}
                                    </p>
                                  </CardContent>
                                </Card>
                              );
                            }

                            return sortedEvents.map((schedule, index) => (
                              <Card key={schedule.id} className="hover:shadow-md transition-shadow">
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between mb-3">
                                    <div>
                                      <h5 className="font-semibold text-gray-900">{schedule.courseName}</h5>
                                      <p className="text-sm text-gray-600">{schedule.instructorName}</p>
                                    </div>
                                    <Badge className={
                                      schedule.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                      schedule.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                      schedule.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                      'bg-gray-100 text-gray-800'
                                    }>
                                      {schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1)}
                                    </Badge>
                                  </div>
                                  
                                  <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                      <Calendar className="h-4 w-4 text-gray-500" />
                                      <span>{format(new Date(schedule.date), 'EEEE, MMMM d, yyyy')}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Clock className="h-4 w-4 text-gray-500" />
                                      <span>{schedule.startTime} - {schedule.endTime} ({schedule.duration} hour{schedule.duration !== 1 ? 's' : ''})</span>
                                    </div>
                                    {schedule.venue && (
                                      <div className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4 text-gray-500" />
                                        <span>{schedule.venue}</span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="flex gap-2 mt-4">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="flex-1"
                                      onClick={() => handleViewDetails(schedule)}
                                    >
                                      View
                                    </Button>
                                    {schedule.status === 'confirmed' && (
                                      <>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="flex-1"
                                          onClick={() => {
                                            setNewDate(new Date(schedule.date));
                                            setNewTime(schedule.startTime);
                                            setEditDialog({ open: true, schedule });
                                          }}
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                                          onClick={() => setCancelDialog({ open: true, schedule })}
                                        >
                                          Cancel
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {schedules.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-xl font-medium mb-2">No Scheduled Lessons</h3>
              <p className="text-gray-500 text-center mb-6 max-w-md">
                You don't have any scheduled lessons yet. Book a lesson through your enrolled courses.
              </p>
            </CardContent>
          </Card>
        ) : filteredAndSortedSchedules.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Search className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-xl font-medium mb-2">No Results Found</h3>
              <p className="text-gray-500 text-center mb-6 max-w-md">
                No schedules match your current search and filter criteria.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredGroupedSchedules ? (
              // Render filtered grouped schedules
              Object.entries(filteredGroupedSchedules).map(([groupKey, groupSchedules]) => (
                <div key={groupKey} className="space-y-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h3 className="font-semibold text-lg">
                      {filter === "groupByCourse" ? groupKey : format(new Date(groupKey), "EEEE, MMMM d, yyyy")}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {groupSchedules.length} lesson{groupSchedules.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  {groupSchedules.map((schedule) => (
                    <Card key={schedule.id} className="overflow-hidden">
                      <CardContent className="p-0">
                        {/* Header with Date and Status */}
                        <div className="bg-drivewise-50 p-4 border-b border-drivewise-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-5 w-5 text-drivewise-600" />
                              <span className="font-medium">
                                {format(new Date(schedule.date), "EEEE, MMMM d, yyyy")}
                              </span>
                            </div>
                            <Badge className={`${getStatusColor(schedule.status)} px-3 py-1`}>
                              {schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1)}
                            </Badge>
                          </div>
                        </div>

                        {/* Course Info Section */}
                        <div className="p-4 border-b border-drivewise-100">
                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="h-5 w-5 text-drivewise-600" />
                            <h3 className="font-semibold text-lg">{schedule.courseName}</h3>
                          </div>
                          <div className="pl-7 space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-600">Type:</span>
                              <span className="font-medium">
                                {schedule.courseType}
                                {schedule.courseSubtype && (
                                  <span className="text-gray-500"> ({schedule.courseSubtype})</span>
                                )}
                              </span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Progress</span>
                                <span className="font-medium">
                                  {schedule.hoursTaken || 0}/{schedule.totalHours || 0} hours
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-drivewise-600 h-2 rounded-full transition-all duration-300" 
                                  style={{ 
                                    width: `${((schedule.hoursTaken || 0) / (schedule.totalHours || 1)) * 100}%` 
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Time and Location Section */}
                        <div className="p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-drivewise-600" />
                            <div>
                              <span className="font-medium">{schedule.startTime} - {schedule.endTime}</span>
                              <span className="text-gray-500 ml-2">({schedule.duration} {schedule.duration === 1 ? "hour" : "hours"})</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-5 w-5 text-drivewise-600" />
                            <span>Instructor: {schedule.instructorName}</span>
                          </div>
                          {schedule.venue && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-5 w-5 text-drivewise-600" />
                              <span>{schedule.venue}</span>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        {schedule.status === "confirmed" && (
                          <div className="p-4 bg-gray-50 border-t border-drivewise-100">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                  setNewDate(new Date(schedule.date));
                                  setNewTime(schedule.startTime);
                                  setEditDialog({ open: true, schedule });
                                }}
                              >
                                Edit Lesson
                              </Button>
                              <Button
                                variant="destructive"
                                className="flex-1"
                                onClick={() => setCancelDialog({ open: true, schedule })}
                              >
                                Cancel Lesson
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ))
            ) : (
              // Render regular filtered schedules with existing recurring logic
              sortedGroups.map(([groupId, groupSchedules]) => {
                if (groupId === 'non-recurring') {
                  return groupSchedules.map((schedule) => (
                    <Card key={schedule.id} className="overflow-hidden">
                      <CardContent className="p-0">
                        {/* Header with Date and Status */}
                        <div className="bg-drivewise-50 p-4 border-b border-drivewise-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-5 w-5 text-drivewise-600" />
                              <span className="font-medium">
                                {format(new Date(schedule.date), "EEEE, MMMM d, yyyy")}
                              </span>
                            </div>
                            <Badge className={`${getStatusColor(schedule.status)} px-3 py-1`}>
                              {schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1)}
                            </Badge>
                          </div>
                        </div>

                        {/* Course Info Section */}
                        <div className="p-4 border-b border-drivewise-100">
                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="h-5 w-5 text-drivewise-600" />
                            <h3 className="font-semibold text-lg">{schedule.courseName}</h3>
                          </div>
                          <div className="pl-7 space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-600">Type:</span>
                              <span className="font-medium">
                                {schedule.courseType}
                                {schedule.courseSubtype && (
                                  <span className="text-gray-500"> ({schedule.courseSubtype})</span>
                                )}
                              </span>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">Progress</span>
                                <span className="font-medium">
                                  {schedule.hoursTaken || 0}/{schedule.totalHours || 0} hours
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-drivewise-600 h-2 rounded-full transition-all duration-300" 
                                  style={{ 
                                    width: `${((schedule.hoursTaken || 0) / (schedule.totalHours || 1)) * 100}%` 
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Time and Location Section */}
                        <div className="p-4 space-y-3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-drivewise-600" />
                            <div>
                              <span className="font-medium">{schedule.startTime} - {schedule.endTime}</span>
                              <span className="text-gray-500 ml-2">({schedule.duration} {schedule.duration === 1 ? "hour" : "hours"})</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-5 w-5 text-drivewise-600" />
                            <span>Instructor: {schedule.instructorName}</span>
                          </div>
                          {schedule.venue && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-5 w-5 text-drivewise-600" />
                              <span>{schedule.venue}</span>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        {schedule.status === "confirmed" && (
                          <div className="p-4 bg-gray-50 border-t border-drivewise-100">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                  setNewDate(new Date(schedule.date));
                                  setNewTime(schedule.startTime);
                                  setEditDialog({ open: true, schedule });
                                }}
                              >
                                Edit Lesson
                              </Button>
                              <Button
                                variant="destructive"
                                className="flex-1"
                                onClick={() => setCancelDialog({ open: true, schedule })}
                              >
                                Cancel Lesson
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ));
                }

                // Sort recurring schedules by week
                const sortedRecurringSchedules = [...groupSchedules].sort((a, b) => 
                  (a.recurringWeek || 0) - (b.recurringWeek || 0)
                );

                const firstSchedule = sortedRecurringSchedules[0];
                const lastSchedule = sortedRecurringSchedules[sortedRecurringSchedules.length - 1];
                const upcomingLessons = sortedRecurringSchedules.filter(s => 
                  new Date(s.date) >= new Date() && s.status !== "cancelled"
                );
                const completedLessons = sortedRecurringSchedules.filter(s => 
                  s.status === "completed"
                );
                const cancelledLessons = sortedRecurringSchedules.filter(s => 
                  s.status === "cancelled"
                );

                return (
                  <Card key={groupId} className="border-2 border-drivewise-200">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Repeat className="h-5 w-5 text-drivewise-600" />
                          <span className="font-medium">Recurring Lessons</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(firstSchedule.status)}>
                            {firstSchedule.status.charAt(0).toUpperCase() + firstSchedule.status.slice(1)}
                          </Badge>
                          {firstSchedule.status === "confirmed" && (
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setNewDate(new Date(firstSchedule.date));
                                  setNewTime(firstSchedule.startTime);
                                  setEditDialog({ open: true, schedule: firstSchedule });
                                }}
                              >
                                Edit Series
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setCancelDialog({ open: true, schedule: firstSchedule })}
                              >
                                Cancel Series
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="bg-drivewise-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="h-4 w-4 text-drivewise-600" />
                            <span className="font-medium">Schedule Pattern</span>
                          </div>
                          <div className="pl-6 space-y-1 text-sm">
                            <p>Every {firstSchedule.recurringDay?.charAt(0).toUpperCase() + firstSchedule.recurringDay?.slice(1)}</p>
                            <p>From {format(new Date(firstSchedule.date), "MMMM d")} to {format(new Date(lastSchedule.date), "MMMM d, yyyy")}</p>
                            <p>Total of {firstSchedule.totalRecurringWeeks} weeks</p>
                            <div className="mt-2 pt-2 border-t border-drivewise-200">
                              <p className="text-drivewise-600">
                                {upcomingLessons.length} upcoming lessons
                                {completedLessons.length > 0 && ` • ${completedLessons.length} completed`}
                                {cancelledLessons.length > 0 && ` • ${cancelledLessons.length} cancelled`}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span>{firstSchedule.startTime} - {firstSchedule.endTime} ({firstSchedule.duration} {firstSchedule.duration === 1 ? "hour" : "hours"})</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-500" />
                            <span>{firstSchedule.instructorName}</span>
                          </div>
                          {firstSchedule.venue && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-gray-500" />
                              <span>{firstSchedule.venue}</span>
                            </div>
                          )}
                          <div className="text-sm text-gray-500">
                            {firstSchedule.courseName}
                          </div>
                        </div>

                        <div className="mt-4">
                          <h4 className="text-sm font-medium mb-2">Upcoming Lessons</h4>
                          <div className="space-y-2">
                            {upcomingLessons.map((schedule) => (
                              <div key={schedule.id} className="flex items-center justify-between text-sm bg-white p-2 rounded-md">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4 text-drivewise-600" />
                                  <div className="flex flex-col">
                                    <span>{format(new Date(schedule.date), "EEEE, MMMM d, yyyy")}</span>
                                    <span className="text-gray-500">{schedule.startTime} - {schedule.endTime}</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    Week {schedule.recurringWeek}
                                  </Badge>
                                  {schedule.status === "confirmed" && (
                                    <div className="flex gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setNewDate(new Date(schedule.date));
                                          setNewTime(schedule.startTime);
                                          setEditDialog({ open: true, schedule });
                                        }}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700"
                                        onClick={() => setCancelDialog({ open: true, schedule })}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, schedule: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editDialog.schedule?.isRecurring ? "Edit Recurring Lesson" : "Edit Lesson"}
            </DialogTitle>
            <DialogDescription>
              {editDialog.schedule?.isRecurring 
                ? "You can edit a single lesson or the entire series. Changes will affect all future lessons in the series."
                : "Select a new date and time for your lesson. Make sure to choose a time that works for both you and your instructor."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-date">New Date</Label>
              <DatePicker
                id="new-date"
                selected={newDate}
                onChange={(date: Date | null) => setNewDate(date)}
                minDate={new Date()}
                maxDate={new Date(new Date().setMonth(new Date().getMonth() + 2))}
                className="w-full p-2 border rounded-md"
                dateFormat="MMMM d, yyyy"
                placeholderText="Select a date"
                aria-label="Select new lesson date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-time">New Time</Label>
              <select
                id="new-time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className="w-full p-2 border rounded-md"
                aria-label="Select new lesson time"
              >
                <option value="">Select a time</option>
                {timeSlots.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditDialog({ open: false, schedule: null })}>
              Cancel
            </Button>
            {editDialog.schedule?.isRecurring && (
              <Button onClick={() => handleEditRecurring(editDialog.schedule!, true)}>
                Update All Lessons
              </Button>
            )}
            <Button onClick={() => handleEditRecurring(editDialog.schedule!, false)}>
              {editDialog.schedule?.isRecurring ? "Update This Lesson" : "Update Lesson"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ open, schedule: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {cancelDialog.schedule?.isRecurring ? "Cancel Recurring Lesson" : "Cancel Lesson"}
            </DialogTitle>
            <DialogDescription>
              {cancelDialog.schedule?.isRecurring 
                ? "You can cancel a single lesson or the entire series. Cancelling the series will remove all future lessons."
                : "Are you sure you want to cancel this lesson? This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              {cancelDialog.schedule?.isRecurring 
                ? "This will affect all future lessons in the series."
                : "Please note that cancelling a lesson may affect your course progress."}
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setCancelDialog({ open: false, schedule: null })}>
              No, Keep Lesson
            </Button>
            {cancelDialog.schedule?.isRecurring && (
              <Button variant="destructive" onClick={() => handleCancelRecurring(cancelDialog.schedule!, true)}>
                Cancel All Lessons
              </Button>
            )}
            <Button variant="destructive" onClick={() => handleCancelRecurring(cancelDialog.schedule!, false)}>
              {cancelDialog.schedule?.isRecurring ? "Cancel This Lesson" : "Yes, Cancel Lesson"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialog.open} onOpenChange={(open) => {
        setViewDetailsDialog({ open, schedule: null });
        if (!open) setStudentDetails(null);
      }}>
        <DialogContent className="max-w-[95vw] md:max-w-2xl max-h-[90vh] overflow-y-auto p-4 md:p-6">
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl">Lesson Details</DialogTitle>
            <DialogDescription className="text-sm md:text-base">
              View detailed information about your lesson.
            </DialogDescription>
          </DialogHeader>
          
          {viewDetailsDialog.schedule && (
            <div className="space-y-4 md:space-y-6">
              {isLoadingStudentDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-drivewise-600"></div>
                    <span>Loading details...</span>
                  </div>
                </div>
              ) : studentDetailsError ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-600">Error loading details: {studentDetailsError}</p>
                </div>
              ) : (
                <>
                  {/* Student Information Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
                        <User className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
                        Student Information
                      </h3>
                      <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Name:</span>
                          <span className="font-medium text-sm md:text-base">{studentDetails?.firstName} {studentDetails?.lastName}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Email:</span>
                          <span className="font-medium text-sm md:text-base break-all">{studentDetails?.email}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Phone:</span>
                          <span className="font-medium text-sm md:text-base">{studentDetails?.phone}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Address:</span>
                          <span className="font-medium text-sm md:text-base break-words">
                            {studentDetails?.address}
                            {studentDetails?.postcode && `, ${studentDetails.postcode}`}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Gearbox:</span>
                          <span className="font-medium text-sm md:text-base">{studentDetails?.gearbox || 'Not specified'}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">License:</span>
                          <span className="font-medium text-sm md:text-base">{studentDetails?.licenseNumber || 'Not provided'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
                        <Calendar className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
                        Lesson Information
                      </h3>
                      <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Date:</span>
                          <span className="font-medium text-sm md:text-base">{format(new Date(viewDetailsDialog.schedule.date), 'EEEE, MMMM d, yyyy')}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Time:</span>
                          <span className="font-medium text-sm md:text-base">{viewDetailsDialog.schedule.startTime} - {viewDetailsDialog.schedule.endTime}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Duration:</span>
                          <span className="font-medium text-sm md:text-base">{viewDetailsDialog.schedule.duration} hour(s)</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-gray-500 text-sm min-w-[80px]">Venue:</span>
                          <span className="font-medium text-sm md:text-base">{viewDetailsDialog.schedule.venue || 'Not specified'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course Information Section */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
                      <BookOpen className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
                      Course Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs md:text-sm text-gray-500">Course Name</div>
                        <div className="text-sm md:text-base font-semibold mt-1 break-words">{viewDetailsDialog.schedule?.courseName || 'Unknown Course'}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs md:text-sm text-gray-500">Course Type</div>
                        <div className="text-sm md:text-base font-semibold mt-1 capitalize">{viewDetailsDialog.schedule?.courseType || 'Not specified'}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs md:text-sm text-gray-500">Course Subtype</div>
                        <div className="text-sm md:text-base font-semibold mt-1 capitalize">{viewDetailsDialog.schedule?.courseSubtype || 'Not specified'}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs md:text-sm text-gray-500">Vehicle Type</div>
                        <div className="text-sm md:text-base font-semibold mt-1">{viewDetailsDialog.schedule?.vehicleType || 'Not specified'}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs md:text-sm text-gray-500">License Type</div>
                        <div className="text-sm md:text-base font-semibold mt-1">{viewDetailsDialog.schedule?.licenseType || 'Not specified'}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-xs md:text-sm text-gray-500">Status</div>
                        <Badge 
                          className={
                            viewDetailsDialog.schedule?.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            viewDetailsDialog.schedule?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            viewDetailsDialog.schedule?.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            viewDetailsDialog.schedule?.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }
                        >
                          {viewDetailsDialog.schedule?.status?.charAt(0).toUpperCase() + viewDetailsDialog.schedule?.status?.slice(1)}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Course Progress Section */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
                      <BarChart className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
                      Course Progress
                    </h3>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs md:text-sm text-gray-500">Hours Taken</div>
                          <div className="text-sm md:text-base font-semibold mt-1">{viewDetailsDialog.schedule?.hoursTaken || 0} hours</div>
                        </div>
                        <div>
                          <div className="text-xs md:text-sm text-gray-500">Total Hours</div>
                          <div className="text-sm md:text-base font-semibold mt-1">{viewDetailsDialog.schedule?.totalHours || 0} hours</div>
                        </div>
                        <div className="col-span-1 sm:col-span-2">
                          <div className="text-xs md:text-sm text-gray-500 mb-2">Progress</div>
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-drivewise-600 h-2.5 rounded-full" 
                              style={{ 
                                width: `${((viewDetailsDialog.schedule?.hoursTaken || 0) / (viewDetailsDialog.schedule?.totalHours || 1)) * 100}%` 
                              }}
                            />
                          </div>
                          <div className="text-xs md:text-sm text-gray-600 mt-1">
                            {viewDetailsDialog.schedule?.hoursTaken || 0} of {viewDetailsDialog.schedule?.totalHours || 0} hours completed
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Student Progress Section */}
                  {studentDetails && (
                    <div className="space-y-2">
                      <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
                        <BarChart className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
                        Student Progress
                      </h3>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <div className="text-xs md:text-sm text-gray-500">Completed Lessons</div>
                            <div className="text-sm md:text-base font-semibold mt-1">{studentDetails.progress?.completedLessons || 0}</div>
                          </div>
                          <div>
                            <div className="text-xs md:text-sm text-gray-500">Total Lessons</div>
                            <div className="text-sm md:text-base font-semibold mt-1">{studentDetails.progress?.totalLessons || 0}</div>
                          </div>
                          <div className="col-span-1 sm:col-span-2">
                            <div className="text-xs md:text-sm text-gray-500">Last Lesson</div>
                            <div className="text-sm md:text-base font-semibold mt-1">{studentDetails.progress?.lastLessonDate || 'No lessons completed'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default StudentSchedule; 